import { useCallback, useEffect, useRef, useState } from 'react';
import { useFileValidation, useImageLoader, useOssUpload } from './hooks';

// 文件处理hook
interface UseFileHandlerOptions {
  mode: 'crop' | 'canvas';
  minPixel?: number;
  maxPixel?: number;
  onUploadComplete?: (url: string) => void;
  onUploadError?: (error: Error) => void;
  onChange?: (value: { url: string; width: number; height: number }) => void;
}

export const useFileHandler = (options: UseFileHandlerOptions) => {
  const { mode, minPixel, maxPixel, onUploadComplete, onUploadError, onChange } = options;

  const { isValidImageFile } = useFileValidation({ minPixel, maxPixel });
  const { uploadFile } = useOssUpload({ onUploadComplete, onUploadError });
  const { handleImageUrl } = useImageLoader();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTriggerFileInput = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return null;

      if (mode === 'canvas' && files.length > 1) {
        // Canvas模式支持多文件处理
        return { type: 'multiple' as const, files: Array.from(files) };
      } else {
        // 单文件处理
        return { type: 'single' as const, file: files[0] };
      }
    },
    [mode],
  );

  const handleUrlDrop = useCallback(
    async (url: string): Promise<{ type: 'single'; file: File } | null> => {
      try {
        const file = await handleImageUrl(url);
        return { type: 'single' as const, file };
      } catch (_error) {
        return null;
      }
    },
    [handleImageUrl],
  );

  const processFile = useCallback(
    async (file: File) => {
      if (!isValidImageFile(file)) return null;

      return new Promise<{ dataUrl: string; width: number; height: number } | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          const img = new Image();

          img.onload = async () => {
            const result = { dataUrl, width: img.width, height: img.height };

            // 自动上传到OSS并返回URL
            try {
              const uploadedUrl = await uploadFile(file, { width: img.width, height: img.height });
              if (onChange) {
                onChange({ url: uploadedUrl, width: result.width, height: result.height });
              }
              resolve({ ...result, dataUrl: uploadedUrl });
            } catch (_error) {
              // 上传失败时使用本地dataURL作为fallback
              if (onChange) {
                onChange({ url: result.dataUrl, width: result.width, height: result.height });
              }
              resolve(result);
            }
          };

          img.onerror = () => resolve(null);
          img.src = dataUrl;
        };

        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });
    },
    [isValidImageFile, uploadFile, onChange],
  );

  return {
    fileInputRef,
    handleTriggerFileInput,
    handleChange,
    handleUrlDrop,
    processFile,
  };
};

// 图片预览状态管理hook
interface UseImagePreviewOptions {
  initialAspectRatio?: number;
  value?: { url: string; width: number; height: number };
}

export const useImagePreview = (options: UseImagePreviewOptions = {}) => {
  const { initialAspectRatio = 1, value } = options;

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number>(initialAspectRatio);
  const [showOverlay, setShowOverlay] = useState(false);

  // 同步外部value
  useEffect(() => {
    if (value?.url) {
      setImagePreview(value.url);
      if (value.width && value.height) {
        setAspectRatio(value.width / value.height);
      }
    } else {
      setImagePreview(null);
      setAspectRatio(initialAspectRatio);
    }
  }, [value, initialAspectRatio]);

  const updatePreview = useCallback((url: string, width?: number, height?: number) => {
    setImagePreview(url);
    if (width && height) {
      setAspectRatio(width / height);
    }
  }, []);

  const clearPreview = useCallback(() => {
    setImagePreview(null);
    setAspectRatio(initialAspectRatio);
    setShowOverlay(false);
  }, [initialAspectRatio]);

  return {
    imagePreview,
    aspectRatio,
    showOverlay,
    setShowOverlay,
    updatePreview,
    clearPreview,
  };
};

// 上传状态管理hook
interface UseUploadStateOptions {
  ref?: React.Ref<HTMLDivElement>;
}

export const useUploadState = (options: UseUploadStateOptions = {}) => {
  const { ref } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  // 连接外部ref
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(containerRef.current);
      } else if (ref && 'current' in ref) {
        (ref as { current: HTMLDivElement | null }).current = containerRef.current;
      }
    }
  }, [ref]);

  return {
    containerRef,
    loading,
    setLoading,
  };
};

// 事件处理工具
export const createEventHandler = (callback: () => void) => {
  return (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    callback();
  };
};
